'use client'

import { Check } from 'lucide-react'

import { BookMeetingButton } from '@/app/(marketing)/components/book-meeting-modal'
import { BRAND_GRADIENT, PillArrow, pillOnNavy, pillSecondary } from '@/components/marketing/brand/brand-ui'
import { MarkerCircle, ScribbleArrow, ScribbleDefs } from '@/components/marketing/brand/scribbles'
import { cn } from '@/lib/utils'

import { plans } from '../pricing-data'

export function PricingPlans() {
  return (
    <div>
      <div className="mt-6 grid items-stretch gap-6 lg:grid-cols-3">
        {plans.map(plan => {
          const featured = plan.id === 'core'
          return (
            <div
              key={plan.id}
              className={cn(
                'relative flex flex-col rounded-xl p-8',
                featured
                  ? 'text-white shadow-[0_10px_30px_rgba(0,0,0,0.2)] lg:-my-4 lg:py-12'
                  : 'border border-[#0F517B]/15 bg-white dark:border-white/10 dark:bg-slate-900'
              )}
              style={featured ? { background: BRAND_GRADIENT } : undefined}
            >
              <h2 className={cn('text-xl font-bold', featured ? 'text-white' : 'text-[#0F517B] dark:text-white')}>
                {plan.name}
              </h2>
              {plan.highlight && <HandHighlight phrase={plan.highlight} note="based on student count" />}
              <p
                className={cn(
                  'mt-3 text-sm leading-relaxed',
                  featured ? 'text-[#D6E6F2]' : 'text-[#0F1B2D]/70 dark:text-slate-300'
                )}
              >
                {plan.blurb}
              </p>

              <div className="relative mt-6">
                <p className="flex items-baseline gap-2">
                  <span
                    className={cn(
                      'font-heading text-5xl tracking-tight',
                      featured ? 'text-white' : 'text-[#0F517B] dark:text-white'
                    )}
                  >
                    {plan.price}
                  </span>
                  {plan.suffix && (
                    <span
                      className={cn('text-sm', featured ? 'text-[#BAD7EC]' : 'text-[#0F1B2D]/60 dark:text-slate-400')}
                    >
                      {plan.suffix}
                    </span>
                  )}
                </p>
                {plan.footnote && (
                  <p
                    className={cn(
                      'mt-1 text-sm font-semibold',
                      featured ? 'text-[#BAD7EC]' : 'text-[#1D6693] dark:text-[#BAD7EC]'
                    )}
                  >
                    {plan.footnote}
                  </p>
                )}
              </div>

              {plan.id === 'trial' ? (
                <a href="/start" className={cn(pillSecondary, 'mt-8 w-full')}>
                  {plan.cta}
                </a>
              ) : (
                <BookMeetingButton className={cn(featured ? pillOnNavy : pillSecondary, 'mt-8 w-full')}>
                  {plan.cta}
                  {featured && <PillArrow />}
                </BookMeetingButton>
              )}

              <ul className="mt-8 space-y-3 text-sm">
                {plan.features.map(feature => (
                  <li
                    key={feature}
                    className={cn('flex gap-3', featured ? 'text-white' : 'text-[#0F1B2D]/80 dark:text-slate-300')}
                  >
                    <Check
                      className={cn(
                        'mt-0.5 size-4 shrink-0',
                        featured ? 'text-[#BAD7EC]' : 'text-[#1D6693] dark:text-[#BAD7EC]'
                      )}
                      strokeWidth={3}
                      aria-hidden="true"
                    />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * The featured plan's takeaway, marked up by hand: the phrase circled, with a
 * note and arrow anchored to it (not to the card), so they stay together at
 * every width.
 */
function HandHighlight({ phrase, note }: { phrase: string; note: string }) {
  return (
    <p className="relative mt-6 text-lg">
      <span className="relative ml-2 inline-block font-bold whitespace-nowrap text-white">
        {phrase}
        <svg
          aria-hidden="true"
          viewBox="0 0 200 60"
          preserveAspectRatio="none"
          className="pointer-events-none absolute -inset-x-3.5 -inset-y-2.5 h-[calc(100%+20px)] w-[calc(100%+28px)]"
        >
          <ScribbleDefs />
          <MarkerCircle cx={100} cy={30} rx={88} ry={22} width={3} rot={-2} seed={31} />
        </svg>
      </span>
      {/* The note sits in the title row, pushed right; the arrow arcs from it back into the circle's right side. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-0 bottom-[calc(100%+14px)] hidden -rotate-6 text-[26px] leading-none font-bold whitespace-nowrap text-white [text-shadow:0_2px_10px_rgba(0,0,0,0.35)] sm:block"
        style={{ fontFamily: 'var(--font-hand)' }}
      >
        {note}
      </span>
      <svg
        aria-hidden="true"
        viewBox="0 0 100 60"
        preserveAspectRatio="none"
        className="pointer-events-none absolute -top-2.5 right-2 left-[188px] hidden h-[50px] sm:block"
      >
        <ScribbleDefs />
        <ScribbleArrow from={[88, 3]} via={[104, 44]} to={[3, 30]} width={4} head={12} seed={31} />
      </svg>
    </p>
  )
}
